import apiV2 from "api-v2";
import { AxiosResponse } from "axios";
import { downloadFileNoPopup } from "helpers/linkHelper";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import { FileableFile } from "types/storage.types";
import { showToast } from "utils/helpers";
import {
	endPerformance,
	logPerformance,
	startPerformance,
} from "utils/logging";

/**
 * The bulk download only needs the RFx id and, when it has already been built,
 * the prepared zip. Kept narrow (rather than a full `Rfx`) so an in-progress
 * draft can be passed without a cast — and so reading any further field here
 * becomes a compile error at every call site instead of a silent runtime undefined.
 */
export type DownloadableRfx = Pick<Rfx, "id"> &
	Partial<Pick<Rfx, "rfxDocumentationZipFile">>;

export const downloadAllDocumentation = async (rfx: DownloadableRfx) => {
	try {
		let file = rfx.rfxDocumentationZipFile;

		if (!file?.file_path) {
			const response = await apiV2.get<AxiosResponse<FileableFile>>(
				`rfq/${rfx.id}/download-documents`
			);

			if (response.status === 200 && response.data?.data) {
				file = response.data.data;
			} else {
				return false;
			}
		}

		downloadFileNoPopup(file.file_path, file.file_name);
	} catch (e) {
		showToast("Failed to download documents", "Error");
		return false;
	}
};

export interface CreateRfxPayload {
	project_id: number;
	supplier_list_id?: number | string;
	template_id?: number | string;
}

export const createRfx = async (
	data: CreateRfxPayload
): Promise<Rfx | null> => {
	try {
		const { data: response } = await apiV2.post("rfq/store", data);
		if (response.status) {
			showToast(response?.message, response?.title, true);
			return response.data as Rfx;
		} else {
			showToast(response?.message, response?.title);
			return null;
		}
	} catch (err: any) {
		const errorData = err?.response?.data;
		if (err?.response?.status === 422 && errorData?.errors) {
			const firstError = Object.values(errorData.errors)[0];
			const errorMessage = Array.isArray(firstError)
				? firstError[0]
				: firstError;
			showToast(
				errorMessage as string,
				errorData?.title || "Validation Error"
			);
		} else {
			showToast(errorData?.message || "An error occurred", "Error");
		}
		return null;
	}
};

export const getRfxById = async (
	id: number,
	includes: string[] | null = null,
	onForbidden?: (error: unknown) => void,
	suppressToastOn403 = false
): Promise<Rfx | null> => {
	const startTime = startPerformance();
	try {
		const request = `rfq/${id}${includes ? `?include=${includes.join(",")}` : ""}`;
		const requestArgs: [string, Record<string, unknown>?] = suppressToastOn403
			? [request, { expectedErrors: [403] }]
			: [request];
		const { data: rfq } = await apiV2.get(...requestArgs);
		if (rfq.status) {
			return rfq.data as Rfx;
		} else {
			return null;
		}
	} catch (err) {
		// Preserve the raw error for callers that need the server-authoritative
		// 403 `reason` (TASK-014 AC3 friendly redirect) — the return contract
		// stays `null` so existing callers (ViewRFQPage) are unaffected.
		onForbidden?.(err);
		return null;
	} finally {
		const endTime = endPerformance();
		logPerformance(startTime, endTime, "getRfxById");
	}
};
